import { useStorage, useLocalStorage } from "@vueuse/core";
import { getAuth, sendPasswordResetEmail, createUserWithEmailAndPassword, onAuthStateChanged, onIdTokenChanged, signInWithEmailAndPassword } from "firebase/auth";
import type { User } from "firebase/auth";
import fbApp from "../../../firebaseInstance";
import { nanoid } from "nanoid";
import dataservice from "../services/dataservice";
import { clearHandoffCookie, isBetaSite, mainSiteSignOutUrl } from "~/modules/core/utils/authHandoff";
import { getResolvedStoreId } from "../composables/useStoreIdentity";
import { ensureComputerIdentity } from "../composables/useComputerIdentity";
import { stopCheckin } from "../utils/checkin";
import { releaseMySeat } from "../../seat/services/seatGuard";

// auth ,  restore, fetch, watch , ready
export const useAuthStore = defineStore("auth", {
  state: () => {
    return {
      uid: "rice",
      computerUuid: "" as any,

      // storeId ที่อยู่ใน firebase id token (custom claim)
      // RTDB rules ใช้ค่านี้ตัดสินสิทธิ์เขียน stores/$storeId/jobchain
      // null = ยังไม่ได้ mint claim หรือยังไม่ได้ login firebase
      storeIdClaim: null as string | null,
    };
  },

  actions: {
    // อ่าน custom claim storeId จาก id token
    //
    // forceRefresh = true จะไปแลก token ใหม่กับ securetoken ของ google
    // ต้องใช้ตอนที่ backend เพิ่ง mint claim ให้ ไม่งั้นจะได้ token เดิมที่ cache ไว้
    // ซึ่งยังไม่มี claim (firebase หมุน token เองทุก ~1 ชม. อยู่แล้ว แต่ช้าไป)
    async syncStoreIdClaim(user: User, forceRefresh = false) {
      try {
        const token = await user.getIdTokenResult(forceRefresh);
        this.storeIdClaim = (token.claims.storeId as string) || null;
      } catch (e) {
        console.error("[auth] อ่าน custom claim ไม่สำเร็จ", e);
        this.storeIdClaim = null;
      }

      return this.storeIdClaim;
    },

    init() {
      // รู้ร้านแล้ว (plugin resolve ไว้ตอน boot) → ใช้ ID ของร้านนี้ ซึ่ง ensure จะ
      // เขียนลง `computerUuid` ให้ ref ข้างล่างอ่านได้ทันที ไม่รู้ร้านก็ใช้ key เดิมไปก่อน
      // shop.init() จะ ensure ซ้ำอีกรอบตอนได้ storeId จริง
      ensureComputerIdentity(getResolvedStoreId());
      const computerUuidRef = useLocalStorage("computerUuid", nanoid(6));
      this.computerUuid = computerUuidRef;


      // ไม่อ่าน cookie `xtable` ของ beta redirect แล้ว — เดิมเจอ cookie ก็ใช้ uid ในนั้นเลย
      // โดยไม่แตะ firebase auth ทำให้ app2 ไม่เคยได้ claim storeId ตอนนี้ทุกโดเมน
      // ต้องมี firebase session ของตัวเอง (ร้าน beta ได้มาจาก utils/authHandoff.ts)
      // และ shop.init() ตรวจว่าร้านตรงกับ claim ก่อนใช้งาน
      const auth = getAuth();


      //return promise whein user is ready
      // claim ที่ถูก mint ใหม่ระหว่าง session จะเข้ามาทางนี้โดยไม่ต้อง reload
      // ไม่ force refresh ตรงนี้ ไม่งั้นจะวนเรียกตัวเองไม่จบ
      onIdTokenChanged(auth, (user) => {
        this.storeIdClaim = null;
        if (user) this.syncStoreIdClaim(user, false);
      });

      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            this.uid = user.uid;
            // force refresh ครั้งเดียวตอนเข้าแอป ให้ได้ claim ล่าสุดจาก backend
            // (เช่นเพิ่งถูก backfill ไปเมื่อกี้) ไม่ต้องรอ token หมดอายุเอง
            await this.syncStoreIdClaim(user, true);
            resolve(user);
          } else {
            // User is signed out
            // window.location.href = '/login'
            useRouter().push("/login");
          }
        });
      });
    },
    async createUserAndStore({ email, password, storeId }, callback) {
      try {
        const auth = getAuth();
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        const user = userCredential.user;
        await _createStore(user, storeId);
        callback && callback(user);
      } catch (error) {
        const errorCode = error.code;
        const errorMessage = error.message;
        alert(errorMessage);
      }
    },

    login(email, password, callback) {
      const auth = getAuth();
      signInWithEmailAndPassword(auth, email, password)
        .then(async (userCredential) => {
          // Signed in
          const user = userCredential.user;
          // ...

          // token ที่เพิ่งออกจากการ login มี claim ล่าสุดอยู่แล้ว แต่ force ไว้
          // เผื่อ backend เพิ่ง mint ให้ระหว่างนั้น ราคาแค่ request เดียว
          await this.syncStoreIdClaim(user, true);

          callback && callback(user);
        })
        .catch((error, abc) => {
          const errorCode = error.code;
          const errorMessage = error.message;

          alert(errorMessage);
        });
    },
    // everywhere = ปุ่ม logout ที่ผู้ใช้กดเอง (layouts/default.vue)
    // ร้าน beta บน app2 ต้องออกจาก xtable.co ด้วย ไม่งั้นเปิด xtable.co ครั้งหน้า
    // จะถูกส่งกลับมา app2 เหมือนไม่เคย logout — ส่งไปหน้า login ของ xtable.co พร้อม
    // ?signout=1 ให้มันออกเอง (firebase session แยกตาม origin ออกแทนกันไม่ได้)
    // logout อัตโนมัติ (rtdbGuard / ร้านไม่ตรง claim) ออกเฉพาะโดเมนนี้
    async logout({ everywhere = false } = {}) {
      // อ่าน from จาก cookie ก่อนล้างทิ้ง
      const signOutUrl = everywhere && isBetaSite() ? mainSiteSignOutUrl() : null;

      clearHandoffCookie()

      // ลบ node online ของเครื่องนี้ก่อน signOut ตอนที่ connection RTDB และ presenceRef
      // ใน shop store ยังอยู่ (ก่อนเด้งไป /login) — rules เปิด online ให้เขียนได้อยู่แล้ว
      // ไม่งั้นเครื่องที่ logout ค้างเป็น "ออนไลน์" จนกว่า tab จะปิด
      await useShopStore().stopCashierOnline();
      // หยุด check-in ทุกนาที (ของที่เขียนไปแล้วเก็บไว้ เป็นประวัติให้ support)
      stopCheckin();

      // คืนที่นั่ง Main / Second ก่อน signOut — POS เป็นเครื่องกลาง logout แล้ว
      // ไม่ควรถือที่นั่งค้างไว้ให้เครื่องอื่นเลือกไม่ได้ (มี timeout ในตัว)
      // หลัง signOut ไม่มี boot มาทำ request ให้แล้ว จึงต้องเรียกตรงตรงนี้
      await releaseMySeat();

      // ล้าง claim ทิ้ง ไม่ให้ค้างไปถึง user คนถัดไปที่มา login บนเครื่องเดียวกัน
      // (POS เป็นเครื่องกลาง เปลี่ยนคนใช้บ่อย)
      this.storeIdClaim = null;

      const auth = getAuth();
      auth
        .signOut()
        .then(() => {
          // Sign-out successful.
          if (signOutUrl) {
            window.location.replace(signOutUrl);
            return;
          }

          window.location.href = "/login";
        })
        .catch((error) => {
          // An error happened.

          alert(error);
        });
    },
    async resetPassword(email:string){
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email)
    }, 

    createUser() {},

    run() {},
  },
});

function _createStore({ uid, email }, storeId = null) {
  return dataservice.store.post({
    name: "My New Store",
    uid: uid,
    email: email,
    storeId: storeId || null,
  });
}
